import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { DroneStatus, LegacyHours, OwnershipType } from '../../../types/api';
import { STATUS_CONFIG, DRONE_STATUS_OPTIONS } from '../tabs/overviewConfig';
import { useDashboardStore } from '../../../stores/dashboardStore';
import { useTranslation } from '../../../i18n';
import { formatDateISO } from '../../../utils/dateUtils';
import { calculateTotalLegacyHours } from '../../../utils/legacyHoursUtils';
import { useUpdateDroneStatusMutation } from './dashboard.mutations';

interface ProfileInfoTableProps {
  totalFiles?: number;
  engineHours?: number | null | undefined;
  flightHours?: number | null | undefined;
  engineStarts?: number | null | undefined;
  firstFlight?: string;
  firstEngineRun?: string;
  droneStatus?: DroneStatus | null | undefined;
  droneSerial?: string;
  onStatusUpdate?: (newStatus: DroneStatus) => void;
  onDataRefresh?: () => void;
  legacyHours?: LegacyHours;
  dateFiltered?: boolean; // true when the profile date slider is narrowed (hours are windowed)
  organizationName?: string | null; // Customer org name for "With customer" drones
  ownershipType?: OwnershipType | null; // SOLD or LEASED for customer-assigned drones
  leaseEndDate?: string | null; // End date for leased drones
  // #1693: the counters include flights other organizations shared; says how many, by whom.
  sharedNote?: string | null;
}

const ProfileInfoTable: React.FC<ProfileInfoTableProps> = ({
  totalFiles = 0,
  engineHours,
  flightHours,
  engineStarts,
  firstFlight,
  firstEngineRun,
  droneStatus,
  droneSerial,
  onStatusUpdate,
  onDataRefresh,
  legacyHours,
  dateFiltered = false,
  organizationName,
  ownershipType,
  leaseEndDate,
  sharedNote,
}) => {
  const { t } = useTranslation();
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [tempStatus, setTempStatus] = useState<DroneStatus | null>(null);

  const updateStatusMutation = useUpdateDroneStatusMutation(droneSerial ?? '');
  const isLoading = updateStatusMutation.isPending;

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return t('common.notAvailable');
    try {
      return formatDateISO(dateString);
    } catch {
      return t('profileTab.invalidDate');
    }
  };

  const formatNumber = (num: number | null | undefined, decimals: number = 0): string => {
    if (num == null) return t('common.notAvailable');
    return num.toFixed(decimals);
  };

  const handleEditStatusClick = () => {
    setTempStatus(droneStatus || null);
    setIsEditingStatus(true);
  };

  const handleCancelStatusEdit = () => {
    setIsEditingStatus(false);
    setTempStatus(null);
  };

  const handleSaveStatus = () => {
    if (!tempStatus || !droneSerial) return;

    updateStatusMutation.mutate(tempStatus, {
      onSuccess: () => {
        onStatusUpdate?.(tempStatus);
        onDataRefresh?.();
        useDashboardStore.getState().refreshOverviewData();
        setIsEditingStatus(false);
        setTempStatus(null);
      },
      onError: () => {
        // Error toast handled globally; just close the edit mode
        console.error('Error updating drone status');
      },
    });
  };

  const handleCloseSuccess = () => {
    updateStatusMutation.reset();
  };

  const legacyTotal = calculateTotalLegacyHours(legacyHours);

  // Lifetime hours = tracked (flights table) + legacy (admin-entered JSONB).
  // The overview cards already display this sum; showing the same total here
  // keeps "Flight/Engine Hours" consistent with them at full slider range.
  // When the date slider is narrowed the tracked figure is windowed, so adding
  // lifetime legacy would produce a number that is neither a range total nor a
  // lifetime total — windowed views show pure tracked hours instead.
  const formatHoursWithLegacy = (
    tracked: number | null | undefined
  ): { value: string; secondary?: string } => {
    // Null tracked means "not loaded / unknown", not zero — keep N/A even when
    // legacy hours exist, so a slow or failed drone-stats fetch can't present
    // the legacy total as a lifetime total with a fabricated "0.0 tracked".
    if (tracked == null) return { value: t('common.notAvailable') };
    if (dateFiltered) return { value: tracked.toFixed(1) };
    const total = tracked + legacyTotal;
    if (legacyTotal === 0) return { value: total.toFixed(1) };
    return {
      value: total.toFixed(1),
      secondary: t('profileTab.hoursBreakdown', {
        tracked: tracked.toFixed(1),
        legacy: legacyTotal.toFixed(1),
      }),
    };
  };

  const flightHoursDisplay = formatHoursWithLegacy(flightHours);
  const engineHoursDisplay = formatHoursWithLegacy(engineHours);

  // Helper to format legacy hours breakdown
  const formatLegacyHours = (): string | null => {
    if (!legacyHours) return null;
    const parts: string[] = [];
    const total = legacyTotal;
    if (total === 0) return null;

    if (legacyHours.pre_2023) parts.push(`pre-2023: ${legacyHours.pre_2023.toFixed(1)}h`);
    if (legacyHours['2023']) parts.push(`2023: ${legacyHours['2023'].toFixed(1)}h`);
    if (legacyHours['2024']) parts.push(`2024: ${legacyHours['2024'].toFixed(1)}h`);
    return `${total.toFixed(1)}h (${parts.join(', ')})`;
  };

  const legacyHoursDisplay = formatLegacyHours();

  // Helper to format ownership display
  const formatOwnership = (): string | null => {
    if (!ownershipType) return null;
    if (ownershipType === 'SOLD') {
      return t('profileTab.ownershipSold');
    }
    if (ownershipType === 'LEASED' && leaseEndDate) {
      return `${t('profileTab.ownershipLeased')} (${t('profileTab.leaseEnds')}: ${leaseEndDate})`;
    }
    return t('profileTab.ownershipLeased');
  };

  // Check if drone is customer-assigned (has organization name, i.e., "With customer" status)
  const isCustomerAssigned = !!organizationName;
  const ownershipDisplay = formatOwnership();

  const tableData = [
    // Add Drone Status as first row if we have drone data
    ...(droneSerial
      ? [
          {
            label: t('profileTab.droneStatusLabel'),
            value: 'DRONE_STATUS_SPECIAL', // Special marker to handle status display separately
            isStatus: true,
          },
        ]
      : []),
    // Add Ownership row only for customer-assigned drones (after Drone Status)
    ...(droneSerial && isCustomerAssigned
      ? [
          {
            label: t('profileTab.ownershipLabel'),
            value: ownershipDisplay || t('common.notAvailable'),
          },
        ]
      : []),
    { label: t('profileTab.firstFlight'), value: formatDate(firstFlight) },
    { label: t('profileTab.firstEngineRun'), value: formatDate(firstEngineRun) },
    {
      label: t('profileTab.flightHours'),
      // What the figure counts (#1612). True for both airborne rules (#1599): the
      // logger's own in-flight flag, and generator RPM for logs that carry none.
      description: t('profileTab.flightHoursDefinition'),
      value: flightHoursDisplay.value,
      secondary: flightHoursDisplay.secondary,
    },
    {
      label: t('profileTab.engineHours'),
      value: engineHoursDisplay.value,
      secondary: engineHoursDisplay.secondary,
    },
    // Add Legacy Hours row only if there are legacy hours
    ...(legacyHoursDisplay
      ? [
          {
            label: 'Legacy Hours',
            value: legacyHoursDisplay,
            tooltip: 'Historical hours from before the system was implemented',
          },
        ]
      : []),
    { label: t('profileTab.engineStarts'), value: formatNumber(engineStarts) },
    { label: t('profileTab.totalFiles'), value: totalFiles.toString() },
  ];

  const getStatusLabel = (status: DroneStatus, orgName?: string | null): string => {
    // Display organization name for "With customer" status if available
    if (status === 'With customer' && orgName) {
      return orgName;
    }
    // Label key comes from the shared status config (single source of truth).
    // Guard the lookup: during a deploy window the backend can return an enum
    // value newer than this baked bundle, so fall back to the raw string rather
    // than throwing (mirrors the prior `statusMap[status] || status` behavior).
    const entry = STATUS_CONFIG[status];
    return entry ? t(entry.labelKey as Parameters<typeof t>[0]) : status;
  };

  const currentStatus = droneStatus
    ? getStatusLabel(droneStatus, organizationName)
    : t('common.notAvailable');
  const statusConfig = droneStatus ? STATUS_CONFIG[droneStatus] : null;

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Table size="small">
          <TableBody>
            {tableData.map((row, index) => (
              <TableRow
                key={row.label}
                sx={{
                  backgroundColor:
                    index % 2 === 0 ? 'transparent' : alpha(BRAND_COLORS.forestGreen.main, 0.02),
                  '&:hover': {
                    backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.05),
                  },
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 500,
                    color: BRAND_COLORS.forestGreen.main,
                    width: '40%',
                    py: 1.5,
                  }}
                >
                  {row.label}
                  {'description' in row && row.description && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      data-testid="profile-row-description"
                      sx={{ fontWeight: 400 }}
                    >
                      {row.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 1.5,
                  }}
                >
                  {row.isStatus ? (
                    // Special handling for Drone Status row
                    isEditingStatus ? (
                      <Box
                        data-testid="drone-status-edit-row"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <Select
                            value={tempStatus || ''}
                            onChange={(e) => setTempStatus(e.target.value as DroneStatus)}
                            disabled={isLoading}
                            inputProps={{ 'data-testid': 'drone-status-select-input' }}
                            sx={{
                              '& .MuiSelect-select': {
                                fontSize: '0.875rem',
                                py: 0.5,
                              },
                            }}
                          >
                            {DRONE_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    color: STATUS_CONFIG[option].color,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: STATUS_CONFIG[option].color,
                                    }}
                                  />
                                  {getStatusLabel(option)}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <IconButton
                          size="small"
                          onClick={handleSaveStatus}
                          disabled={isLoading || !tempStatus}
                          data-testid="drone-status-save-btn"
                          sx={{ color: '#4caf50' }}
                        >
                          {isLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            <CheckIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={handleCancelStatusEdit}
                          disabled={isLoading}
                          sx={{ color: '#f44336' }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          data-testid="drone-status-chip"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: statusConfig?.color || '#666',
                            backgroundColor: statusConfig
                              ? alpha(statusConfig.color, 0.1)
                              : alpha('#666', 0.1),
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: statusConfig?.color || '#666',
                            }}
                          />
                          {currentStatus}
                        </Box>

                        {droneSerial && (
                          <IconButton
                            size="small"
                            onClick={handleEditStatusClick}
                            data-testid="drone-status-edit-btn"
                            sx={{
                              color: BRAND_COLORS.forestGreen.main,
                              '&:hover': {
                                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                              },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    )
                  ) : 'tooltip' in row && row.tooltip ? (
                    <Tooltip title={row.tooltip} arrow>
                      <span>{row.value}</span>
                    </Tooltip>
                  ) : (
                    <>
                      {row.value}
                      {'secondary' in row && row.secondary && (
                        <>
                          {' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            {row.secondary}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {sharedNote && (
        <Typography
          variant="caption"
          display="block"
          color="text.secondary"
          data-testid="profile-shared-note"
          sx={{ mt: 1 }}
        >
          {sharedNote}
        </Typography>
      )}

      {/* Success notification */}
      <Snackbar
        open={updateStatusMutation.isSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success">
          {t('profileTab.statusUpdated', { status: tempStatus ?? droneStatus ?? '' })}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileInfoTable;
